import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList, toggleClass } from 'react-classlist-helper'
import { createPortal } from 'react-dom'
import { Redirect, RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'

import JobsiteHomepageContainer from '~/client/src/mobile/components/JobsiteHomepageContainer/JobsiteHomepageContainer'
import MobilePhotoDetails from '~/client/src/mobile/components/PhotoDetails/MobilePhotoDetails'
import Sidebar from '~/client/src/mobile/components/Sidebar/Sidebar'
import routes from '~/client/src/mobile/constants/mobileRoutes'
import DeliveryDetails from '~/client/src/mobile/views/DeliveryDetails/DeliveryDetailsView'
import DeliveryTicket from '~/client/src/mobile/views/DeliveryTicket/DeliveryTicket'
import Documents from '~/client/src/mobile/views/Documents/Documents'
import Deliveries from '~/client/src/mobile/views/MobileDeliveriesView'
import Notifications from '~/client/src/mobile/views/Notifications/NotificationsList'
import { ProjectsView } from '~/client/src/mobile/views/Projects/Projects'
import SendDeliveryTicketView from '~/client/src/mobile/views/SendDeliveryTicketView/SendDeliveryTicketView'
import {
  Content,
  Footer,
  Header,
  View,
} from '~/client/src/shared/components/Layout'
import WrappedRoute from '~/client/src/shared/components/WrappedRoute'
import { FilePreviewProvider } from '~/client/src/shared/contexts/FilePreviewContext'
import IRoute from '~/client/src/shared/interfaces/IRoute'

import { withErrorBoundary } from '../shared/components/ErrorBoundary'
import GlobalUserCardUnsafe from '../shared/components/GlobalUserCard/GlobalUserCard'
import { Loader } from '../shared/components/Loader'
import { LinkedResourceEdit } from '../shared/components/ResourceLink'
import SavePasswordDialog from '../shared/components/SavePassword/SavePasswordDialog'
import {
  appendLocationSearchAndHash,
  getTransitionPath,
} from '../shared/constants/commonRoutes'
import Localization from '../shared/localization/LocalizationManager'
import RoutesStore from '../shared/stores/ui/Routes.store'
import InfoView from '../shared/views/InfoView/InfoView'
import GetTheAppBanner, {
  shouldShowBanner,
} from './components/GetTheAppBanner/GetTheAppBanner'
import MobileFullScreenPreview from './components/MobileFullscreenFilePreview/MobileFullscreenPreview'
import MobileResourceEditDrawer from './components/MobileResource/MobileResourceEditDrawer'
import OneTimePopup from './components/OneTimePopup/OneTimePopup'
import SwitchWithSlide from './components/SwitchWithSlide/SwitchWithSlide'
import MobileInitialState from './stores/MobileInitialState'
import MobileCommonStore from './stores/ui/MobileCommon.store'
import AddTeammatesView from './views/AddTeammates/AddTeammates'
import ChatV2View from './views/ChatV2View/ChatV2View'
import DeliverySharedView from './views/EntitySharedView/DeliverySharedView'
import FormSharedView from './views/EntitySharedView/FormSharedView'
import MobileEquipmentView from './views/Equipment/MobileEquipmentView'
import CompactForms from './views/Forms/CompactForms'
import MobileLocationsView from './views/Locations/MobileLocationsView'
import MobileMaterialResourcesView from './views/MaterialResources/MobileMaterialResourcesView'
import NotificationsSettingsView from './views/NotificationsSettings/NotificationsSettingsView'
import PermitTypesView from './views/PermitTypesView/PermitTypesView'
import PermitView from './views/PermitView'
import QRCodeAccessStandalone from './views/QRCodes/QRCodeAccessStandalone'
import QRCodes from './views/QRCodes/QRCodes'
import MobileStruxTagsView from './views/StruxTags/MobileStruxTagsView'
import UserProfileView from './views/UserProfileView/UserProfileView'
import UsersDirectoryView from './views/UsersDirectoryView/UsersDirectoryView'

const GlobalUserCard = withErrorBoundary(GlobalUserCardUnsafe)

const mobileRoutes: IRoute[] = [
  {
    path: routes.FORMS,
    exact: true,
    component: CompactForms,
    isProtected: true,

    isFormsRelated: true,
  },
  {
    path: routes.FORM_VIEW,
    exact: false,
    component: PermitView,
    isProtected: true,

    isFormsRelated: true,
  },
  {
    path: routes.FORM_TYPES,
    component: PermitTypesView,
    isProtected: true,

    isFormsRelated: true,
  },
  {
    path: routes.HOME,
    exact: true,
    component: JobsiteHomepageContainer,
    isProtected: true,

    isLogisticsRelated: true,
  },
  {
    path: routes.NOTIFICATIONS,
    exact: false,
    component: Notifications,
    isProtected: true,
  },
  {
    path: routes.DELIVERIES,
    exact: true,
    component: Deliveries,
    isProtected: true,

    isDeliveriesRelated: true,
  },
  {
    path: routes.DELIVERY_DETAILS,
    exact: false,
    component: DeliveryDetails,
    unprotectedAltComponent: DeliveryTicket,
    isProtected: true,

    isDeliveriesRelated: true,
  },
  {
    path: routes.SHARED_DELIVERY_DETAILS,
    exact: false,
    component: DeliverySharedView,
    isProtected: true,

    isDeliveriesRelated: true,
  },
  {
    path: routes.SHARED_FORM_DETAILS,
    exact: false,
    component: FormSharedView,
    isProtected: true,

    isDeliveriesRelated: false,
    isFormsRelated: true,
  },
  {
    path: routes.DOCUMENTS,
    exact: false,
    component: Documents,
    isProtected: true,
  },
  {
    path: routes.PHOTO_DETAIL,
    exact: false,
    component: MobilePhotoDetails,
    isProtected: true,
  },
  {
    path: routes.PROJECTS,
    exact: false,
    component: ProjectsView,
    isPhotosRelated: true,
  },
  {
    path: routes.USERS_DIRECTORY,
    exact: true,
    component: UsersDirectoryView,
    isProtected: true,
  },
  {
    path: routes.QR_CODES,
    exact: true,
    component: QRCodes,
    isProtected: true,
    isQRCodeRelated: true,
  },
  {
    path: routes.QR_CODE_ACCESS,
    exact: false,
    component: QRCodeAccessStandalone,
    isProtected: true,
    isQRCodeRelated: true,
    isAdminOnly: true,
  },
  {
    path: routes.NOTIFICATION_SETTINGS,
    exact: false,
    component: NotificationsSettingsView,
    isProtected: true,
  },
  {
    path: routes.USER_PROFILE,
    exact: false,
    component: UserProfileView,
    isProtected: true,
  },
  {
    path: routes.SEND_SMS_DELIVERY_TICKET,
    exact: false,
    component: SendDeliveryTicketView,
    isDeliveriesRelated: true,
  },
  {
    path: routes.INFO,
    exact: false,
    component: InfoView,
  },
  {
    path: routes.CHAT,
    component: ChatV2View,
    isProtected: true,
  },
  {
    path: routes.ADD_TEAMMATE,
    component: AddTeammatesView,
    isProtected: true,
    isQRCodeRelated: true,
  },
  {
    path: routes.EQUIPMENT,
    exact: true,
    component: MobileEquipmentView,
    isProtected: true,
    isEquipmentRelated: true,
  },
  {
    path: routes.MATERIAL_RESOURCES,
    exact: true,
    component: MobileMaterialResourcesView,
    isProtected: true,
    isMaterialResourcesRelated: true,
  },
  {
    path: routes.LOCATIONS,
    exact: true,
    component: MobileLocationsView,
    isProtected: true,
    isLocationsRelated: true,
  },
  {
    path: routes.STRUXTAGS,
    exact: true,
    component: MobileStruxTagsView,
    isProtected: true,
    isStruxTagsRelated: true,
  },
]

interface IMobileRootViewProps {
  state?: MobileInitialState
  common?: MobileCommonStore
}

@inject('common', 'state')
@observer
class MobileRootView extends React.Component<
  IMobileRootViewProps & RouteComponentProps
> {
  private readonly store: RoutesStore = null
  @observable private isBannerHidden = !shouldShowBanner()

  public constructor(props: IMobileRootViewProps & RouteComponentProps) {
    super(props)
    this.store = new RoutesStore(props.state)
    props.common.initHistory(props.history)
    makeObservable(this)
  }

  public render() {
    return <main className="full-height">{this.content}</main>
  }

  public get content(): JSX.Element {
    const { isFullHeight, isSidebarOpen, toggleSidebar } = this.props.common

    if (this.store.areProjectTypesLoading) {
      return (
        <View>
          <Content scrollable>
            <div className="absolute-block" style={{ top: '50%' }}>
              <div className="col x-center y-center">
                <Loader size="md" />
                <span>{Localization.translator.loading}</span>
              </div>
            </div>
          </Content>
        </View>
      )
    }

    const isProjectSelected = !!this.props.state.activeOrInitProjectCode

    return (
      <View>
        {isProjectSelected && (
          <>
            <GetTheAppBanner
              hidden={this.isBannerHidden}
              onDismiss={this.handleBannerDismiss}
            />
            <OneTimePopup />
          </>
        )}
        <Header>
          <Sidebar />
          <div
            onClick={toggleSidebar}
            className={classList({
              dimmer: true,
              shown: isSidebarOpen,
            })}
          />
        </Header>
        <div
          className={classList({
            'layout-view-root col': true,
            'displayed-header': !isFullHeight,
            'full-height': isFullHeight,
          })}
        >
          <Content
            scrollable
            className={toggleClass('with-banner', !this.isBannerHidden)}
          >
            <FilePreviewProvider isMobile>
              <MobileFullScreenPreview />
              <MobileRoutes />
            </FilePreviewProvider>
          </Content>
        </div>
        <Footer />
      </View>
    )
  }

  @action.bound
  private handleBannerDismiss() {
    this.isBannerHidden = true
  }
}

export default withRouter(MobileRootView)

interface IMobileRoutes {
  state?: MobileInitialState
  common?: MobileCommonStore
}

@inject('state', 'common')
@observer
class MobileRoutes extends React.Component<IMobileRoutes> {
  public get routes() {
    const {
      isDeliveriesDisabled,
      isTrackerDisabled,
      isLogisticsDisabled,
      isWorkflowsDisabled,
      isEquipmentDisabled,
      isStruxTagsDisabled,
      isMaterialResourcesDisabled,
      isLocationsDisabled,
      userActiveProjectSettings,
    } = this.props.state

    return mobileRoutes.filter(
      route =>
        (!isDeliveriesDisabled || !route.isDeliveriesRelated) &&
        (!isTrackerDisabled || !route.isTrackerRelated) &&
        (userActiveProjectSettings?.hasScanAccess || !route.isQRCodeRelated) &&
        (userActiveProjectSettings?.isSuperUser || !route.isAdminOnly) &&
        (!isLogisticsDisabled || !route.isLogisticsRelated) &&
        (!isWorkflowsDisabled || !route.isFormsRelated) &&
        (!isEquipmentDisabled || !route.isEquipmentRelated) &&
        (!isMaterialResourcesDisabled || !route.isMaterialResourcesRelated) &&
        (!isStruxTagsDisabled || !route.isStruxTagsRelated) &&
        (!isLocationsDisabled || !route.isLocationsRelated) &&
        (!isTrackerDisabled || !route.isTrackerRelated),
    )
  }

  public render() {
    const { state, common } = this.props

    const redirectToUrl = common.viewToDisplay

    // If no project code, redirect to projects list instead of creating invalid path
    const projectCode = state.activeOrInitProjectCode
    const redirectTarget = appendLocationSearchAndHash(
      projectCode
        ? getTransitionPath(redirectToUrl, projectCode)
        : routes.PROJECTS,
    )

    return (
      <>
        <SavePasswordDialog />
        {state.idOfUserDisplayedOnGlobalCard &&
          createPortal(
            // `pointer-events: auto` overrides the `none` Radix Dialog sets on
            // `body` while the resource drawer is open — without it, clicks on
            // the user card pass through to the drawer's dismiss handler.
            // `data-user-card-portal` lets the drawer's `onPointerDownOutside`
            // recognize clicks inside this layer and skip its own dismiss.
            <div data-user-card-portal="" style={{ pointerEvents: 'auto' }}>
              <GlobalUserCard userId={state.idOfUserDisplayedOnGlobalCard} />
            </div>,
            document.body,
          )}
        {state.resourceDisplayedOnGlobalCard && (
          <LinkedResourceEdit
            open
            recordId={state.resourceDisplayedOnGlobalCard.id}
            projectId={state.activeProject?.id}
            resourceTypeId={state.resourceDisplayedOnGlobalCard.resourceTypeId}
            onClose={state.resetResourceDisplayedOnGlobalCard}
            shell={children => (
              <MobileResourceEditDrawer
                open
                onClose={state.resetResourceDisplayedOnGlobalCard}
              >
                {children}
              </MobileResourceEditDrawer>
            )}
          />
        )}
        <SwitchWithSlide>
          {this.routes.map(route => (
            <WrappedRoute
              key={route.path}
              {...route}
              defaultViewToDisplay="/"
            />
          ))}
          <Redirect to={redirectTarget} />
        </SwitchWithSlide>
      </>
    )
  }
}
